import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  getUserDocumentReviews,
  getDocumentReviewById,
  deleteDocumentReview,
  getDocumentReviewStats,
  type DocumentReview,
  type AnalysisStatus,
} from "@/shared/db";
import { useAuth } from "../../../contexts/AuthContext";

export function useDocumentReview(reviewId?: string) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [review, setReview] = useState<DocumentReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () => {
      if (reviewId && user) {
        fetchReview();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reviewId, user]
  );

  const fetchReview = async () => {
    if (!reviewId || !user) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getDocumentReviewById(reviewId);
      if (data) {
        setReview(data);
      } else {
        setError(t("documentReview.errors.notFound"));
      }
    } catch (err: unknown) {
      console.error("Error fetching review:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("documentReview.errors.fetchFailed");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteReview = async (id: string) => {
    try {
      await deleteDocumentReview(id);
      toast.success(t("documentReview.success.deleted"));
      return true;
    } catch (err: unknown) {
      console.error("Error deleting review:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("documentReview.errors.deleteFailed");
      toast.error(errorMessage);
      return false;
    }
  };

  const refetch = () => {
    fetchReview();
  };

  return {
    review,
    loading,
    error,
    deleteReview,
    refetch,
  };
}

export function useDocumentReviews(filters?: {
  country?: string;
  documentType?: string;
  status?: AnalysisStatus;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [reviews, setReviews] = useState<DocumentReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Serialize filters to create a stable dependency key
  const filtersKey = useMemo(
    () => JSON.stringify(filters ?? {}),
    [filters?.country, filters?.documentType, filters?.status]
  );

  const fetchReviews = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getUserDocumentReviews(user.id, filters);
      setReviews(data);
    } catch (err: unknown) {
      console.error("Error fetching reviews:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("documentReview.errors.fetchFailed");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user, filtersKey, t]);

  useEffect(() => {
    if (user) {
      fetchReviews();
    }
  }, [user, fetchReviews]);

  const refetch = useCallback(() => {
    fetchReviews();
  }, [fetchReviews]);

  return {
    reviews,
    loading,
    error,
    refetch,
  };
}

export function useDocumentReviewStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<{
    total: number;
    completed: number;
    pending: number;
    failed: number;
    byRisk: {
      low: number;
      medium: number;
      high: number;
    };
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(
    () => {
      if (user) {
        fetchStats();
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const fetchStats = async () => {
    if (!user) return;

    setLoading(true);

    try {
      const data = await getDocumentReviewStats(user.id);
      setStats(data);
    } catch (err: unknown) {
      console.error(
        "Error fetching stats:",
        err instanceof Error ? err.message : "Unknown error"
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    stats,
    loading,
    refetch: fetchStats,
  };
}
